/**
 * The four figures above a list of jobs.
 *
 * Jonathon, 2026-08-19: "i want some examples of how we can improve the look
 * and visibility". Every list screen told you one thing — how many rows there
 * are — and the money behind those rows was only ever visible one job at a
 * time. This is the same idea as the sold board's `scoreboard`, aimed at the
 * lists that are not sold yet: what is on the list, what it is worth, what has
 * already been signed this month, and how many customers nobody has called.
 *
 * The value comes in already computed. `computeLadder` is the only thing
 * allowed to say what a job is worth (CLAUDE.md rule 1), so the caller runs it
 * per estimate and hands the totals over — exactly as `MasterList` does. This
 * module decides which jobs are counted where, and nothing else.
 */

import { followUpStatus, type FollowUpInput } from "./followup.js";
import { isTestJob } from "./lost.js";
import { addCents, cents, type Cents } from "./money.js";
import { dateFilterStart, type DateFilter } from "./search.js";
import { asLocalInstant, localDateString } from "./signedDate.js";
import { SOLD_STAGES } from "./stage.js";
import type { JobStage } from "./types.js";

/** What `listSummary` needs off a job. A `Job` satisfies it. */
export interface ListSummaryJob extends FollowUpInput {
  id: string;
  stage: JobStage;
  is_test?: boolean | null;
  /** Set when the signed contract was attached or e-signed. */
  signed_contract_at?: string | null;
  /** Set when the job was handed to production. */
  sends_at?: string | null;
}

/**
 * The day a job counts as sold.
 *
 * Jonathon, 2026-09-02: "sends date or approved date if there's no sends or
 * approved date can also count as the signed date if needed."
 *
 * A signed date is the answer whenever there is one, and the other two only
 * ever stand in for a missing one. The company reaches "sold" by more than one
 * route — `jobIsWon` in estimates.ts has said so since it was written — and
 * three jobs were sitting in production with no signed date at all, worth
 * nothing to a figure that only knew how to read that one field.
 *
 * The order is the order of how close each is to the signature:
 *
 *   `signed_contract_at`   the day the customer signed. The truth, when known.
 *   `approved_at`          the estimate was approved. The e-signature stamps
 *                          this at the same moment it stamps the signed date,
 *                          so it only does work when somebody approved an
 *                          estimate by hand and never dated the contract.
 *   `sends_at`             handed to production. Always at or after signing,
 *                          so it is the last resort — but it is the one that
 *                          actually rescues the jobs that were missing.
 *
 * Preferring the signed date over the approved one is not a detail: an
 * estimate can be approved long after the contract was signed, and job 1004
 * was signed in August and approved in September. Reading the approval first
 * would move that sale into the wrong month.
 */
export function soldDate(
  job: Pick<ListSummaryJob, "signed_contract_at" | "sends_at">,
  approvedAt?: string | null,
): string | null {
  return job.signed_contract_at ?? approvedAt ?? job.sends_at ?? null;
}

/**
 * Did the job sell inside the window a date filter describes?
 *
 * The sold list's "This month" used to ask when the lead came in, the same
 * question the lead lists ask. On a sold list that is the wrong question:
 * Jonathon, 2026-10-23, "the sold this month seems low". It was missing nine
 * of the nineteen jobs sold in October 2026, every one a lead from September
 * that signed in October. A sale belongs to the window it was made in, which
 * is `soldDate` above.
 *
 * A job with no sold date at all is only on the list under "All time".
 */
export function soldWithinDateFilter(
  job: Pick<ListSummaryJob, "signed_contract_at" | "sends_at">,
  filter: DateFilter,
  approvedAt?: string | null,
  now: Date = new Date(),
): boolean {
  const start = dateFilterStart(filter, now);
  if (!start) return true;
  const sold = soldDate(job, approvedAt);
  if (!sold) return false;
  return asLocalInstant(sold) >= start;
}

export interface ListSummary {
  /** Rows on the list, test jobs included — it is what you can see. */
  jobs: number;
  /** Contract dollars on the rows that have not been sold yet. */
  pipeline: Cents;
  /** Rows behind `pipeline`. */
  pipelineJobs: number;
  /** Contract dollars on rows signed in the calendar month `now` falls in. */
  signed: Cents;
  /** Rows behind `signed`. */
  signedJobs: number;
  /** Rows whose follow-up clock has gone red. */
  needCall: number;
}

export const emptyListSummary = (): ListSummary => ({
  jobs: 0,
  pipeline: cents(0),
  pipelineJobs: 0,
  signed: cents(0),
  signedJobs: 0,
  needCall: 0,
});

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Did `timestamp` land in the same calendar month as `now`, where we are?
 *
 * `jobs.signed_contract_at` is a `date`, so what arrives is "2026-09-01" and
 * not a timestamp. `new Date` reads a bare date as midnight **UTC**, which
 * everywhere in the United States is the evening before — so a contract signed
 * on the first of the month was read as the last day of the month before it,
 * and dropped out of the figure that was meant to count it. Three of the four
 * jobs signed in September 2026 were signed on the 1st.
 *
 * A date column carries no time and no zone, so there is nothing to convert:
 * the string already says which month it belongs to, and comparing the two
 * calendar strings is the whole answer. A real timestamp still gets read as an
 * instant, because that is what it is.
 */
export function inCurrentMonth(
  timestamp: string | null | undefined,
  now: Date,
): boolean {
  if (!timestamp) return false;
  const month = localDateString(now).slice(0, 7);
  if (DATE_ONLY.test(timestamp)) return timestamp.slice(0, 7) === month;
  const at = new Date(timestamp);
  if (Number.isNaN(at.getTime())) return false;
  return localDateString(at).slice(0, 7) === month;
}

/**
 * Add up the jobs on screen.
 *
 * `values` is contract dollars per job id — the sum of `computeLadder(...)
 * .grandTotal` across the job's active estimates, because a multi-option job
 * sells more than one. A job missing from the map is worth nothing yet, which
 * is the honest answer for a lead with no estimate on it.
 *
 * Test jobs are counted as rows and left out of every figure that is money.
 * That is the same line `MasterList` draws, and the reason `is_test` exists.
 *
 * `now` is passed in rather than read from the clock so this stays pure and
 * "this month" is testable.
 *
 * `approvals` is when each job's active estimate was approved, for the jobs
 * where that is known. It stands in for a missing signed date and nothing
 * else — `soldDate` above has the order and the reason. It is a map rather
 * than a field on the job because `approved_at` belongs to the estimate, and a
 * job may have several.
 */
export function listSummary(
  jobs: readonly ListSummaryJob[],
  values: ReadonlyMap<string, Cents>,
  now: Date,
  approvals: ReadonlyMap<string, string> = new Map(),
): ListSummary {
  const summary = emptyListSummary();
  summary.jobs = jobs.length;

  const pipeline: Cents[] = [];
  const signed: Cents[] = [];

  for (const job of jobs) {
    // `now`, not the wall clock. `followUpStatus` defaults its second argument
    // to `new Date()`, so leaving it off made this one figure read the real
    // clock while the other three read `now` — the function was not the pure
    // thing its own doc comment promised, and the test below only passed for
    // the 24 hours after the date it pins.
    if (followUpStatus(job, now).level === "alert") summary.needCall++;
    if (isTestJob(job)) continue;

    const value = values.get(job.id) ?? cents(0);

    if (inCurrentMonth(soldDate(job, approvals.get(job.id)), now)) {
      signed.push(value);
      summary.signedJobs++;
    }

    // Sold work is no longer waiting on anybody, so it is not "in the
    // pipeline" — it would double with the signed figure beside it and make a
    // busy month look like a full pipeline.
    if (!SOLD_STAGES.includes(job.stage)) {
      pipeline.push(value);
      summary.pipelineJobs++;
    }
  }

  summary.pipeline = addCents(...pipeline);
  summary.signed = addCents(...signed);
  return summary;
}
